import React from "react";
import { BaseCard } from "./CommunityCard";
import { ViewMode } from "@/store/zustand/communityPanelStore";
import { LayoutOption } from "@/utils/types";
import { TagListItem } from "./TagListItem";

interface FeaturedViewProps {
  viewMode: ViewMode;
  selectedLayout: string | null;
  setSelectedLayout: (layoutId: string | null) => void;
  selectedCategory: string | null;
  setSelectedCategory: (categoryId: string | null) => void;
  componentCounts: Record<string, number>;
  handleFilterChange: () => void;
  layoutOptions: LayoutOption[];
  leftPanelFilter: string;
}

export const FeaturedView = React.memo(function FeaturedView({
  viewMode,
  selectedLayout,
  setSelectedLayout,
  selectedCategory,
  setSelectedCategory,
  componentCounts,
  handleFilterChange,
  layoutOptions,
  leftPanelFilter,
}: FeaturedViewProps): React.ReactNode {
  const handleSelect = (layoutId: string) => {
    const newSelectedLayout = selectedLayout === layoutId ? null : layoutId;
    setSelectedLayout(newSelectedLayout);
    // Ensure tags are deselected when a layout filter is changed
    setSelectedCategory(null);
    handleFilterChange();
  };

  const filteredLayoutOptions = React.useMemo(() => {
    if (!leftPanelFilter) {
      return layoutOptions;
    }
    const lowerCaseFilter = leftPanelFilter.toLowerCase();
    return layoutOptions.filter((layout) =>
      layout.name.toLowerCase().includes(lowerCaseFilter),
    );
  }, [layoutOptions, leftPanelFilter]);

  if (viewMode === ViewMode.Grid) {
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2 p-4 overflow-y-scroll h-full auto-rows-min w-full shrink-0 scrollbar-hide">
        {filteredLayoutOptions.map((layout) => (
          <BaseCard
            key={layout.id}
            title={layout.name}
            imageUrl={layout.icon || undefined}
            count={componentCounts[layout.id] || 0}
            selected={selectedCategory === layout.id}
            onClick={() => {
              setSelectedCategory(
                selectedCategory === layout.id ? null : layout.id,
              );
              handleFilterChange();
            }}
          />
        ))}
      </div>
    );
  }

  if (viewMode === ViewMode.List) {
    return (
      <div className="p-2 px-2 flex flex-col w-full gap-1">
        {filteredLayoutOptions.map((layout) => (
          <TagListItem
            key={layout.id}
            item={{
              displayName: layout.name,
              id: layout.id,
              url: null,
            }}
            isSelected={selectedLayout === layout.id}
            // Use componentCounts.tags as layouts are now tags
            count={componentCounts[layout.id] || 0}
            onSelect={() => handleSelect(layout.id)}
          />
        ))}
      </div>
    );
  }

  return null;
});

export default FeaturedView;
